<template>
  <div>
    <!--
      Stack sentinel. Tagged with Vuetify's internal `v-dialog__content--active`
      so the stackable mixin's getMaxZIndex() counts this preloaded modal and
      stacks every later dialog above it (e.g. add-new-client over new-deal).
      z-index comes from the CLASS (`z-[204]`), never an inline style, so
      ModalService.closeModalAction's cssText truncation cannot corrupt it; it
      matches .preloaded-modal so later dialogs compute 206 and nest above.
      COUPLING: depends on a Vuetify 2.7 internal class name — revisit on any
      Vuetify 3 migration.
    -->
    <div
      v-if="isVisible"
      class="preloaded-stack-sentinel v-dialog__content--active pointer-events-none fixed z-[204] h-0 w-0"
      aria-hidden="true"
    />
    <div
      v-if="isVisible"
      class="preloaded-backdrop fixed inset-0 z-[203] bg-black/50"
      @click="handleBackdropClick"
    />
    <!--
      Parked, the modal is only visually gone (opacity + pointer-events); without
      these it stays in the accessibility tree and in tab order, so keyboard
      focus could land inside an invisible cross-origin iframe.
    -->
    <div
      :class="modalClass"
      :style="modalStyle"
      :aria-modal="isVisible ? 'true' : 'false'"
      :aria-hidden="isVisible ? undefined : 'true'"
      role="dialog"
    >
      <div class="preloaded-iframe-wrapper relative h-full w-full overflow-hidden">
        <!-- bg-core-surface is load-bearing: the overlay must stay opaque so the
             child's white boot/suspense frame never shows through the loader. -->
        <div
          v-if="isVisible && isContentLoading"
          class="preloaded-iframe-loader absolute inset-0 z-[2] flex items-center justify-center bg-core-surface"
        >
          <PreloadedIframeRiveLoader />
        </div>
        <iframe
          v-if="iframeSrc"
          ref="iframeRef"
          title="Preloaded V3 Iframe"
          loading="eager"
          data-preloaded="true"
          :data-active-modal-type="activeModalType"
          class="preloaded-iframe h-full w-full border-0"
          allow="autofocus; storage-access"
          :tabindex="isVisible ? undefined : -1"
          :src="iframeSrc"
          @load="handleIframeLoad"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import GoToService from '@/router/GoToService';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePreloadedIframe } from '@/composables/usePreloadedIframe';
import AuthService from '@/modules/user/AuthService';
import PreloadedIframeRiveLoader from '@/components/PreloadedIframeRiveLoader.vue';

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeSrc = ref('');

const {
  isVisible,
  isFullscreen,
  isContentLoading,
  currentModalName,
  modalOptions,
  setIframeElement,
  closeModal,
  sendPing,
} = usePreloadedIframe();

// Drives F2's type-aware resolver: present (= the open modal's type) only while
// visible; cleared when parked so a parked iframe never steals legacy messages.
const activeModalType = computed(() =>
  isVisible.value ? currentModalName.value ?? undefined : undefined,
);

const isPersistent = computed(() => modalOptions.value.persistent !== false);
const isFullscreenActive = computed(
  () => isVisible.value && (isFullscreen.value || !!modalOptions.value.fullscreen),
);
const modalClass = computed(() => [
  'preloaded-modal',
  isVisible.value ? 'preloaded-modal--open' : 'preloaded-modal--parked',
  modalOptions.value.contentClass || '',
  { 'iframe-modal-fullscreen': isFullscreenActive.value },
]);
const modalStyle = computed(() => {
  if (!isVisible.value || isFullscreenActive.value) {
    return {};
  }
  const width = modalOptions.value.maxWidth;
  const maxWidth = typeof width === 'number' ? `${width}px` : width;
  return { maxWidth };
});

const getPreloadedIframeSrc = (withCacheBuster = false) => {
  const baseUrl = GoToService.getLendiV3Url('iframe-host', true);
  if (!withCacheBuster) {
    return baseUrl;
  }
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('ts', String(Date.now()));
    return url.toString();
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}ts=${Date.now()}`;
  }
};

const scheduleIframeLoad = () => {
  const setSrc = () => {
    iframeSrc.value = getPreloadedIframeSrc();
  };
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(setSrc, { timeout: 1000 });
  } else {
    setTimeout(setSrc, 1000);
  }
};

const prewarmIframeHost = () => {
  try {
    const url = new URL(GoToService.getLendiV3Url('iframe-host', true));
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = url.origin;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
  } catch (error) {
    console.error('[PreloadedV3Iframe] prewarm failed', error);
  }
};

const closeIfAllowed = () => {
  if (!isPersistent.value) {
    closeModal(null);
  }
};
const handleBackdropClick = () => closeIfAllowed();
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !isVisible.value) return;
  closeIfAllowed();
};
const handleIframeLoad = () => sendPing();

watch(iframeRef, (value) => setIframeElement(value));

// Account switch: reload the preloaded iframe so it picks up fresh auth tokens.
watch(AuthService.userStateV2Key, (newKey, oldKey) => {
  if (newKey === oldKey) return;
  closeModal(null);
  iframeSrc.value = '';
  // A macrotask, deliberately not requestAnimationFrame: rAF never runs while
  // the tab is hidden, so an identity change in a background tab (a login or an
  // org switch, but also the ordinary boot race where the user resolves after
  // this component mounts) left iframeSrc empty for as long as the tab stayed
  // hidden. setTimeout still lands after Vue's microtask flush, so the old
  // iframe is unmounted before the new src mounts a fresh one.
  setTimeout(() => {
    iframeSrc.value = getPreloadedIframeSrc(true);
  }, 0);
});

onMounted(() => {
  prewarmIframeHost();
  scheduleIframeLoad();
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  setIframeElement(null);
});
</script>

<style scoped lang="scss">
/*
  Deliberately still SCSS. Two unlayered global rules in App.vue target this
  same element: `.delegation-modal-content` (sizing, no !important) and
  `.iframe-modal-fullscreen`. Tailwind v4 emits utilities inside a cascade
  layer, and layered declarations lose to unlayered ones regardless of
  specificity — so expressing the sizing below as utilities would silently hand
  height/max-height authority to `.delegation-modal-content`. Scoped selectors
  keep it with the component. The min()/max()/calc() sizing would also only
  become long arbitrary-value strings, which reads worse than the declarations.
*/
.preloaded-modal {
  position: fixed;
  z-index: 204; // from a class, never inline — see the sentinel comment above
  overflow: hidden;
  background-color: var(--token-core-surface);
  border-radius: 16px;
}
.preloaded-modal--open {
  top: 50%;
  left: 50%;
  /* Same vh/vw → dvh/dvw pattern as *-viewport utilities in tailwind.css. */
  width: min(calc(100vw - 32px), 962px);
  width: min(calc(100dvw - 32px), 962px);
  height: max(80vh, min(752px, 98vh));
  height: max(80dvh, min(752px, 98dvh));
  max-height: 94vh;
  max-height: 94dvh;
  transform: translate(-50%, -50%) scale(1);
  pointer-events: auto;
  opacity: 1;
}
.preloaded-modal--parked {
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  opacity: 0.01;
  transform: translate(-50%, -50%) scale(0.95);
  pointer-events: none;
}
.preloaded-backdrop {
  backdrop-filter: blur(5px) !important;
}
</style>
